import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintForTrd = makeArticle({
  slug: "is-saint-for-treatment-resistant-depression",
  title: "Is SAINT® for Treatment-Resistant Depression? | Neuro Wellness",
  description:
    "SAINT® TMS is FDA-cleared for adults with major depressive disorder who haven't improved on antidepressants. Learn if SAINT may be appropriate for treatment-resistant depression.",
  canonical: "/learn/is-saint-for-treatment-resistant-depression",
  h1: "Is SAINT® TMS for Treatment-Resistant Depression?",
  breadcrumbLabel: "SAINT® for Treatment-Resistant Depression",
  shortAnswer:
    "Yes. SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is FDA-cleared for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. This means SAINT® is specifically indicated for people whose depression has not responded adequately to medication — a common definition of treatment-resistant depression. Eligibility is determined by a psychiatrist after a clinical evaluation.",
  keyTakeaways: [
    "SAINT® is FDA-cleared for adults whose depression has not improved on antidepressant medication.",
    "The FDA-cleared indication aligns with common definitions of treatment-resistant depression.",
    "SAINT® uses fMRI-guided targeting and delivers 50 sessions in 5 days.",
    "Eligibility requires a psychiatric evaluation and MRI safety screening.",
    "SAINT® does not guarantee results — individual outcomes vary.",
  ],
  sections: [
    {
      heading: "SAINT® and the FDA-Cleared Indication",
      paragraphs: [
        "The FDA cleared the Magnus Neuromodulation System with SAINT Technology (510(k) K220177) in September 2022 for adults with major depressive disorder who have failed to achieve satisfactory improvement from prior antidepressant medication in the current episode. This indication directly addresses the population commonly described as having treatment-resistant depression.",
        "Treatment-resistant depression is typically defined as major depressive disorder that has not responded adequately to at least two antidepressant trials of adequate dose and duration. SAINT®'s FDA-cleared indication covers adults in this situation, though the final determination of eligibility is made by a psychiatrist.",
      ],
    },
    {
      heading: "How SAINT® Differs from Antidepressants",
      paragraphs: [
        "Antidepressants work through chemical neurotransmitter systems — primarily serotonin, norepinephrine, and dopamine. SAINT® uses magnetic pulses to stimulate a specific brain region identified by functional MRI, working through a completely different mechanism. This is why SAINT® may help when medications that target chemical systems have not.",
        "SAINT® delivers intermittent theta burst stimulation — ten sessions per day for five consecutive days, with each session lasting about 10 minutes. The stimulation target is personalized based on each patient's own brain connectivity, not a population average.",
      ],
    },
    {
      heading: "Who May Be Evaluated for SAINT®",
      paragraphs: ["SAINT® may be appropriate for adults who:"],
      list: [
        "Have major depressive disorder that has not improved adequately on antidepressant medication.",
        "Can safely undergo an MRI (required for targeting).",
        "Do not have ferromagnetic metal in or near the head, implanted electronic devices, or other MRI/TMS contraindications.",
        "Do not have untreated bipolar disorder, active psychosis, or other conditions that may affect eligibility.",
        "Are willing to commit to five consecutive treatment days.",
      ],
    },
    {
      heading: "What This Means for Patients",
      paragraphs: [
        "If you have treatment-resistant depression, SAINT® is one of the options a psychiatrist may discuss with you. It is not the only option — conventional TMS, SPRAVATO®, medication adjustments, and ECT are also evidence-based approaches. The right choice depends on your individual history, preferences, and clinical circumstances.",
        "A consultation with a psychiatrist is the best way to determine whether SAINT® may be appropriate for you. The evaluation includes reviewing your diagnosis, medication history, and overall health, and screening for conditions that may affect eligibility.",
      ],
      links: [
        { to: "/saint", text: "Learn more about SAINT® TMS" },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Is SAINT® TMS for treatment-resistant depression?",
      answer:
        "Yes. SAINT® is FDA-cleared for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. This aligns with common definitions of treatment-resistant depression. Eligibility is determined by a psychiatrist after a clinical evaluation.",
    },
    {
      question: "Do I need treatment-resistant depression to receive SAINT®?",
      answer:
        "The FDA-cleared indication requires that you have not achieved satisfactory improvement from prior antidepressant medication in the current episode. This typically means at least one adequate antidepressant trial has failed, though many SAINT® patients have tried multiple medications. A psychiatrist determines whether you meet the clinical criteria.",
    },
    {
      question: "Can SAINT® work if antidepressants didn't?",
      answer:
        "SAINT® may help when antidepressants have not, because it works through a different mechanism — magnetic brain stimulation rather than chemical neurotransmitter modulation. In the 2022 randomized trial, 78.6% of the active group met remission criteria at four weeks versus 13.3% of the sham group. These are trial results, not guaranteed outcomes, and individual response varies.",
    },
    {
      question: "What if I've already tried TMS and it didn't work?",
      answer:
        "A prior course of standard TMS does not necessarily exclude you from SAINT®. SAINT® differs from conventional TMS in targeting (fMRI-guided vs. scalp measurement), schedule (5 days vs. 6 weeks), and pulse pattern (accelerated theta burst). A psychiatrist can review your TMS history and determine whether SAINT® may be appropriate.",
    },
    {
      question: "If you are having thoughts of self-harm, what should you do?",
      answer:
        "Call or text 988 (Suicide & Crisis Lifeline) now, or go to your nearest emergency room. Help is available 24/7.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology (September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: saintQuestionRelated,
});
